// @ts-nocheck
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import Icon from 'customer-flows/components/ui-kit/icon'
import { getOwner } from '@ember/owner'
import { lookupStore } from 'customer-flows/utils/core'
import ValueInspector from './value-inspector'
import { service } from '@ember/service'
import type Owner from '@ember/owner'

const storeModules = import.meta.glob('../../../stores/**/*.ts', { eager: false })
const STORE_NAMES: StoreName[] = Object.keys(storeModules)
  .map((path) => path.replace('../../../stores/', '').replace('.ts', ''))
  .filter((name) => name !== 'base-store')

export default class StoreInspectorComponent extends Component {
  @service declare session: any

  @tracked expandedStores: Set<string> = new Set()

  constructor(owner: Owner, args: object) {
    super(owner, args)
    const saved = this.session.restore()['debugToolbarExpandedStores']
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        this.expandedStores = new Set(parsed)
      } catch (e) {
        // ignore parse errors
      }
    }
  }

  @action
  toggleStore(storeName: string) {
    if (this.expandedStores.has(storeName)) {
      this.expandedStores.delete(storeName)
    } else {
      this.expandedStores.add(storeName)
    }
    this.expandedStores = new Set(this.expandedStores)
    this.session.persist({
      debugToolbarExpandedStores: JSON.stringify(Array.from(this.expandedStores))
    })
  }

  isStoreExpanded = (storeName: string) => {
    return this.expandedStores.has(storeName)
  }

  getStoreIcon = (storeName: string) => {
    return this.expandedStores.has(storeName) ? 'carat-down' : 'carat-forward'
  }



  isInjectedService = (value: any) => {
    if (!value || typeof value !== 'object') return false

    const constructorName = value.constructor?.name || ''

    return (
      constructorName.endsWith('Service') ||
      constructorName.endsWith('Manager') ||
      constructorName.endsWith('Router') ||
      constructorName.endsWith('Builder') ||
      constructorName.endsWith('Tracker') ||
      constructorName.endsWith('Logger') ||
      constructorName === 'Owner' ||
      value.isService === true
    )
  }

  getStoreData = (storeName: StoreName) => {
    try {
      const owner = getOwner(this)
      const store = lookupStore(owner, storeName)
      const data: Record<string, any> = {}

      const allKeys = new Set<string>()

      let obj = store
      while (obj && obj !== Object.prototype) {
        Object.getOwnPropertyNames(obj).forEach(key => allKeys.add(key))
        const descriptors = Object.getOwnPropertyDescriptors(obj)
        Object.keys(descriptors).forEach(key => {
          if (descriptors[key].get) {
            allKeys.add(key)
          }
        })
        obj = Object.getPrototypeOf(obj)
      }

      for (const key of allKeys) {
        if (key.startsWith('_') || key === 'constructor' || key === 'isDestroyed' || key === 'isDestroying') continue

        try {
          const value = store[key]

          if (typeof value === 'function') continue

          if (this.isInjectedService(value)) continue

          if (value === null || value === undefined) {
            data[key] = value
          } else if (typeof value === 'object') {
            if (value.constructor?.name === 'Map') {
              data[key] = Array.from(value.entries()).reduce((obj, [k, v]) => {
                obj[k] = v
                return obj
              }, {})
            } else if (value.constructor?.name === 'Set') {
              data[key] = Array.from(value)
            } else if (value.value !== undefined && value.state !== undefined) {
              data[key] = value.value
            } else {
              data[key] = value
            }
          } else {
            data[key] = value
          }
        } catch (e) {
          data[key] = `Error: ${e.message}`
        }
      }

      return data
    } catch (e) {
      return { error: e.message }
    }
  }

  <template>
    <div data-testid='store-inspector' class='flex h-full w-full flex-col overflow-auto p-4'>
      <h2 class='text-xbold-para-16 mb-3 text-neutrals-400'>Stores</h2>

      <div class='flex flex-col gap-3'>
        {{#each STORE_NAMES as |storeName|}}
          <div class='rounded-3 overflow-hidden border border-neutrals-100 bg-neutrals-50'>
            <div
              class='flex cursor-pointer items-center justify-between bg-theme-50 px-4 py-3 transition-colors hover:bg-theme-100'
              {{on 'click' (fn this.toggleStore storeName)}}
            >
              <span class='text-bold-para-14 text-neutrals-400'>{{storeName}}</span>
              <Icon @src={{this.getStoreIcon storeName}} class='h-4 w-4' />
            </div>
            {{#if (this.isStoreExpanded storeName)}}
              <div class='flex flex-col gap-2 p-4'>
                {{#each-in (this.getStoreData storeName) as |key value|}}
                  <div class='flex flex-col gap-1'>
                    <span class='text-bold-para-12 text-neutrals-400'>{{key}}</span>
                    <ValueInspector @value={{value}} />
                  </div>
                {{/each-in}}
              </div>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}

